/**
 * ViewModeToggle — PR-X2 (May 2026).
 *
 * Shadcn Tabs wrapper that syncs the active tab with the `view` URL search
 * param. Used in the TeamDetail page header to flip between Practice and
 * Game-day shells.
 *
 * Why URL-param-only state (not DB, not localStorage):
 *   - View mode is ephemeral; a coach may switch back and forth multiple
 *     times per day. Persisting it adds zero value and a migration burden.
 *   - URL is bookmarkable and shareable (deep-link to a team in Game-day
 *     view is useful for coordination).
 *   - Avoids any need to "overload batches with lineup semantics" — the
 *     mode lives entirely in the client's navigation state.
 *
 * Why Tabs (not Switch):
 *   - Content below changes substantially between modes (different sections,
 *     different actions). Tabs read as "two distinct screens" rather than a
 *     binary preference.
 *
 * Default = practice when `?view=` is absent or unrecognized.
 */

import { useSearchParams } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

export type TeamViewMode = "practice" | "gameday";

/**
 * Pure helper — exported for use in TeamDetail and unit tests so the rule
 * "unknown / missing → practice" stays in one place.
 */
export function parseViewMode(raw: string | null): TeamViewMode {
  return raw === "gameday" ? "gameday" : "practice";
}

export function ViewModeToggle() {
  const [searchParams, setSearchParams] = useSearchParams();
  const mode = parseViewMode(searchParams.get("view"));

  const handleChange = (next: string) => {
    const params = new URLSearchParams(searchParams);
    if (next === "gameday") {
      params.set("view", "gameday");
    } else {
      // Practice is the default; omit param for a cleaner URL.
      params.delete("view");
    }
    setSearchParams(params, { replace: false });
  };

  return (
    <Tabs value={mode} onValueChange={handleChange} className="w-fit">
      <TabsList data-testid="view-mode-toggle">
        <TabsTrigger value="practice" data-testid="view-mode-practice">
          Practice
        </TabsTrigger>
        <TabsTrigger value="gameday" data-testid="view-mode-gameday">
          Game-day
        </TabsTrigger>
      </TabsList>
    </Tabs>
  );
}
